import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../../components/ui/Button';
import CleanHeading from '../../../components/ui/HolographicText';
import usePerformanceOptimization from '../../../hooks/usePerformanceOptimization';

const HeroSection = () => {
  const [currentHeadline, setCurrentHeadline] = useState(0);
  const { shouldReduceAnimations, getOptimizedProps } = usePerformanceOptimization();

  const headlines = [
    "Telecommunications Engineering Excellence",
    "Infrastructure Solutions That Connect",
    "Precision Components for Tomorrow"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeadline((prev) => (prev + 1) % headlines.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
      {/* Telecom Network Background Animations */}
      <div className="absolute inset-0">
        {/* Network Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(90deg, rgb(59 130 246) 1px, transparent 1px),
              linear-gradient(rgb(59 130 246) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        {/* Animated Data Packets */}
        {!shouldReduceAnimations && (
          <>
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={`packet-${i}`}
                className="absolute w-2 h-2 bg-telecom-primary/40 rounded-sm"
                style={{
                  left: `${10 + (i * 12)}%`,
                  top: `${20 + Math.sin(i) * 15}%`,
                }}
                animate={{
                  x: [0, 100, 200, 300],
                  opacity: [0, 1, 1, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  delay: i * 1.2,
                  ease: "linear"
                }}
              />
            ))}

            {/* Signal Transmission Lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
              <defs>
                <linearGradient id="signalGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="transparent" />
                  <stop offset="50%" stopColor="rgb(59 130 246)" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="transparent" />
                </linearGradient>
              </defs>

              {/* Horizontal transmission lines */}
              <motion.line
                x1="0%" y1="25%" x2="100%" y2="25%"
                stroke="url(#signalGradient)"
                strokeWidth="1"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.line
                x1="0%" y1="75%" x2="100%" y2="75%"
                stroke="url(#signalGradient)"
                strokeWidth="1"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
            </svg>

            {/* Network Connection Nodes */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={`node-${i}`}
                className="absolute w-3 h-3 border-2 border-telecom-primary/30 rounded-full bg-white/80"
                style={{
                  left: `${15 + (i * 15)}%`,
                  top: `${30 + (i % 2) * 40}%`,
                }}
                animate={{
                  scale: [1, 1.2, 1],
                  borderColor: [
                    'rgba(59, 130, 246, 0.3)',
                    'rgba(59, 130, 246, 0.6)',
                    'rgba(59, 130, 246, 0.3)'
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.5,
                  ease: "easeInOut"
                }}
              />
            ))}

            {/* Signal Wave Ripples */}
            <div className="absolute top-1/4 right-1/4">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={`ripple-${i}`}
                  className="absolute w-16 h-16 border border-telecom-secondary/20 rounded-full"
                  style={{
                    left: '50%',
                    top: '50%',
                    marginLeft: '-32px',
                    marginTop: '-32px',
                  }}
                  animate={{
                    scale: [0, 2, 0],
                    opacity: [0.6, 0, 0.6],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: i * 1.3,
                    ease: "easeOut"
                  }}
                />
              ))}
            </div>

            {/* Data Flow Indicators */}
            <div className="absolute bottom-1/3 left-1/5">
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={`flow-${i}`}
                  className="absolute w-1 h-8 bg-gradient-to-t from-telecom-accent/40 to-transparent"
                  style={{
                    left: `${i * 8}px`,
                  }}
                  animate={{
                    scaleY: [0.3, 1, 0.3],
                    opacity: [0.4, 0.8, 0.4],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: "easeInOut"
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="space-y-8"
          {...getOptimizedProps({
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.6, ease: "easeOut" }
          })}
        >
          {/* Company Brand */}
          <motion.div
            {...getOptimizedProps({
              initial: { opacity: 0, y: 10 },
              animate: { opacity: 1, y: 0 },
              transition: { duration: 0.5, delay: 0.2 }
            })}
            className="pt-24"
          >
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-brand-primary"></div>
              <span className="text-brand-primary text-lg font-bold tracking-wider uppercase">
                ONS METTLE
              </span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-brand-primary"></div>
            </div>
            <p className="text-brand-neutral text-sm font-medium tracking-wide">
              Engineering Excellence • Innovation • Trust
            </p>
          </motion.div>

          {/* Dynamic Headlines */}
          <div className="h-32 sm:h-40 md:h-48 flex items-center justify-center relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentHeadline}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="absolute inset-0 flex items-center justify-center"
              >
                {/* Custom responsive heading with explicit sizing */}
                <h1 className="font-heading font-bold text-foreground leading-tight max-w-4xl px-4 text-center
                               text-2xl 
                               xs:text-2xl 
                               sm:text-2xl 
                               md:text-6xl 
                               lg:text-5xl 
                               xl:text-5xl
                               2xl:text-5xl">
                  {headlines[currentHeadline]}
                </h1>
              </motion.div>
            </AnimatePresence>

            {/* Headline Indicators */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {headlines.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentHeadline
                    ? 'bg-brand-primary'
                    : 'bg-border hover:bg-brand-neutral'
                    }`}
                  onClick={() => setCurrentHeadline(index)}
                />
              ))}
            </div>
          </div>

          {/* Subtitle */}
          <motion.div
            {...getOptimizedProps({
              initial: { opacity: 0, y: 15 },
              animate: { opacity: 1, y: 0 },
              transition: { duration: 0.5, delay: 0.3 }
            })}
          >
            <p className="text-lg sm:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
              With over 10 years of specialized expertise in telecommunications components and network infrastructure,
              we deliver <span className="text-telecom-primary font-semibold">precision-engineered solutions</span> that
              connect your vision to tomorrow's technology.
            </p>
          </motion.div>

          {/* Call-to-Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Link to="/contact-engagement">
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                className="w-full sm:w-auto"
              >
                Get Started Today
              </Button>
            </Link>

            <Link to="/featured-projects">
              <Button
                variant="outline"
                size="lg"
                iconName="Eye"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                View Our Projects
              </Button>
            </Link>
          </motion.div>

          {/* Key Stats */}
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-4 gap-8 mt-16 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <div className="text-center">
              <div className="text-3xl font-bold text-telecom-primary mb-2">10+</div>
              <p className="text-sm text-text-secondary">Years Experience</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-telecom-secondary mb-2">500+</div>
              <p className="text-sm text-text-secondary">Components Delivered</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-telecom-accent mb-2">99.9%</div>
              <p className="text-sm text-text-secondary">Reliability Rate</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-brand-primary mb-2">50+</div>
              <p className="text-sm text-text-secondary">Telecom Partners</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;